import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Users, IndianRupee, Package, Edit, Clock, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDate } from "@/lib/utils";
import { formatCurrency as baseCurrencyFormat } from "@/lib/utils";
import EditWeddingDialog from "@/components/EditWeddingDialog";

export const WeddingCard = ({ wedding, onUpdate }) => {
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Use wedding's currency for formatting
  const formatCurrency = (amount) => baseCurrencyFormat(amount, wedding.currency || 'INR');
  
  const budgetPercentage = (wedding.spent / wedding.budget) * 100;
  const remainingBudget = wedding.budget - wedding.spent;
  const daysUntil = Math.ceil((new Date(wedding.wedding_date) - new Date()) / (1000 * 60 * 60 * 24));

  const handleCardClick = () => {
    navigate(`/wedding/${wedding.id}`);
  };

  const handleEditClick = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    setIsEditDialogOpen(true);
  };

  return (
    <>
      <Card 
        onClick={handleCardClick}
        className="cursor-pointer transition-smooth hover:shadow-strong border-t-4 border-t-primary group overflow-hidden bg-card"
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-smooth">
                {wedding.bride_name} & {wedding.groom_name}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleEditClick}
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Edit className="h-4 w-4" />
              </Button>
              {wedding.is_trial && (
                <Badge className="bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20 border-yellow-500/20">
                  Trial
                </Badge>
              )}
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                {daysUntil} days
              </Badge>
            </div>
          </div>

        {/* Trial Expiry Warning */}
        {wedding.is_trial && wedding.trial_expired && (
          <Alert className="mb-4 bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              Trial expired. Upgrade to continue editing.
            </AlertDescription>
          </Alert>
        )}
        
        {wedding.is_trial && !wedding.trial_expired && wedding.trial_days_remaining !== null && (
          <Alert className="mb-4 bg-blue-50 border-blue-200">
            <Clock className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              {wedding.trial_days_remaining} day{wedding.trial_days_remaining !== 1 ? 's' : ''} remaining in trial
            </AlertDescription>
          </Alert>
        )}

        {/* Details */}
        <div className="space-y-3 mb-5">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 text-primary" />
            <span>{formatDate(wedding.wedding_date)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 text-accent" />
            <span className="line-clamp-1">{wedding.venue}</span>
          </div>
        </div>

        {/* Budget Progress */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="text-muted-foreground">Budget Progress</span>
            <span className="font-semibold text-foreground">
              {formatCurrency(wedding.spent)} / {formatCurrency(wedding.budget)}
            </span>
          </div>
          <Progress value={budgetPercentage} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {budgetPercentage.toFixed(1)}% used
          </p>
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
          <div className="flex flex-col items-center">
            <Users className="h-5 w-5 text-accent mb-1" />
            <span className="text-lg font-bold text-foreground">{wedding.guest_count || 0}</span>
            <span className="text-xs text-muted-foreground">Guests</span>
          </div>
          <div className="flex flex-col items-center">
            <Package className="h-5 w-5 text-gold mb-1" />
            <span className="text-lg font-bold text-foreground">{wedding.vendor_count || 0}</span>
            <span className="text-xs text-muted-foreground">Vendors</span>
          </div>
          <div className="flex flex-col items-center">
            <IndianRupee className="h-5 w-5 text-success mb-1" />
            <span className="text-lg font-bold text-foreground">{formatCurrency(remainingBudget)}</span>
            <span className="text-xs text-muted-foreground">Remaining</span>
          </div>
        </div>
      </div>
    </Card>

    <EditWeddingDialog
      open={isEditDialogOpen}
      onOpenChange={setIsEditDialogOpen}
      wedding={wedding}
      onUpdate={onUpdate}
    />
  </>
  );
};

export default WeddingCard;